// Persistent cart: share ONE cart across a customer's devices.
//
// The cart itself always lives in Shopify. We only persist a POINTER to it -
// the cart token - against the customer, so a second device resolves to the
// same cart instead of building its own. Shopify then stays the single source
// of truth and there is nothing to diff, merge or reconcile.
//
// Why a token and not the line items: the token does NOT change when the cart
// contents change (quantity 1 -> 2 keeps the same token). It only changes when
// a new cart is created. That keeps metafield writes to roughly one per cart
// lifetime instead of one per click, which is what made the previous
// item-list-syncing implementation unusable.

const CART_ROOT_DOMAIN = '.bouwmaat.nl';
const CART_COOKIE_DAYS = 30;
// Set to the token we adopted, so a bad/stale pointer cannot reload forever.
const CART_SWAP_GUARD_KEY = 'bm_cart_adopted_token';
// Pre-existing sentinel meaning "this customer has no saved cart".
const NO_CART_SENTINEL = 'nullcart';
// Last customer this device was signed in as, so a logout or an account
// switch can be detected on the next load.
const LAST_CUSTOMER_KEY = 'bm_last_customer_id';
// The cart token last known to belong to a signed-in account on this device.
// Lets a logout be detected even when LAST_CUSTOMER_KEY was never written -
// e.g. the shopper only used the account app, where this script does not run.
const ACCOUNT_TOKEN_KEY = 'bm_account_cart_token';

function getPersistentCartCustomerId() {
    return (window.ShopifyTranslations && window.ShopifyTranslations.customerId) || '';
}

function readCartCookieToken() {
    const match = document.cookie.match(/(?:^|;\s*)cart=([^;]*)/);
    if (!match) return '';
    try {
        return decodeURIComponent(match[1]);
    } catch (e) {
        return match[1];
    }
}

function writeCartCookieToken(token) {
    // Shopify sets its own `cart` cookie host-scoped (no Domain attribute).
    // Ours is domain-wide so it can be shared with the account app, but the
    // two are different cookies - and when both are sent the host-scoped one
    // wins. Expire it first, or adopting another device's cart silently does
    // nothing because the stale narrower cookie keeps winning.
    document.cookie = `cart=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=None; Secure=true`;
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + CART_COOKIE_DAYS);
    document.cookie = `cart=${encodeURIComponent(token)}; domain=${CART_ROOT_DOMAIN}; path=/; expires=${expiryDate.toUTCString()}; SameSite=None; Secure=true`;
}

function clearCartCookie() {
    // Both scopes: Shopify's own cookie is host-scoped and a narrower cookie of
    // the same name wins when the browser sends both, so clearing only the
    // domain-wide one leaves the old cart in play.
    document.cookie = `cart=; domain=${CART_ROOT_DOMAIN}; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=None; Secure=true`;
    document.cookie = `cart=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=None; Secure=true`;
}

function readAccountToken() {
    try {
        return localStorage.getItem(ACCOUNT_TOKEN_KEY) || '';
    } catch (e) {
        return '';
    }
}

function writeAccountToken(token) {
    try {
        if (token) localStorage.setItem(ACCOUNT_TOKEN_KEY, token);
        else localStorage.removeItem(ACCOUNT_TOKEN_KEY);
    } catch (e) { /* private mode */ }
}

function readLastCustomerId() {
    try {
        return localStorage.getItem(LAST_CUSTOMER_KEY) || '';
    } catch (e) {
        return '';
    }
}

function writeLastCustomerId(customerId) {
    try {
        localStorage.setItem(LAST_CUSTOMER_KEY, customerId || '');
    } catch (e) { /* private mode */ }
}

/**
 * Move this device off the account's cart and onto a copy of its own.
 *
 * Called when the signed-in customer changes - a logout, or switching
 * accounts. Without it the device keeps the same cart token, so edits made
 * while signed out still land on the previous customer's cart and show up on
 * their other devices.
 *
 * The items are copied so the shopper does not visibly lose their cart; the
 * account keeps the original, which is also what lets a later login restore
 * products that were removed while signed out.
 */
async function detachFromAccountCart(cart) {
    const items = (cart && cart.items) || [];
    const previousToken = (cart && cart.token) || '';
    clearCartCookie();

    if (items.length) {
        // Only copy into a cart that is demonstrably NOT the one we just left.
        // If clearing the cookie did not take - a second `cart` cookie at
        // another scope, a blocked write - then adding here would add the
        // lines back into the same cart and double every quantity. Re-read
        // first and bail out rather than risk that.
        let detached = false;
        try {
            const check = await fetch('/cart.js', {headers: {Accept: 'application/json'}});
            if (check.ok) {
                const current = await check.json();
                detached = current.token !== previousToken;
            }
        } catch (e) {
            detached = false;
        }

        if (detached) {
            try {
                await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        items: items.map((item) => ({
                            id: item.variant_id,
                            quantity: item.quantity,
                            properties: item.properties || {},
                        })),
                    }),
                });
            } catch (e) {
                // Losing the copy is safe; leaking edits onto the account's
                // cart is not. Reload either way.
                console.error('Persistent cart: failed to copy cart on detach', e);
            }
        } else {
            console.error(
                'Persistent cart: still on the previous cart after clearing the ' +
                    'cookie - skipping the copy so quantities are not doubled.',
            );
        }
    }

    window.location.reload();
}


function readCurrentCart() {
    return (window.CartStore && typeof window.CartStore.get === 'function')
        ? window.CartStore.get()
        : fetch('/cart.json').then((response) => response.json());
}

// The selected store lives in localStorage + cookies, i.e. per device, so
// unlike cart lines and `attributes['delivery-method']` it does NOT travel
// with the cart. We carry it alongside the token so an adopting device ends
// up on the same store as the one that saved the cart.
function readAdoptionGuard() {
    try {
        return sessionStorage.getItem(CART_SWAP_GUARD_KEY) || '';
    } catch (e) {
        return '';
    }
}

function writeAdoptionGuard(token) {
    try {
        sessionStorage.setItem(CART_SWAP_GUARD_KEY, token);
    } catch (e) { /* private mode */ }
}

function clearAdoptionGuard() {
    try {
        sessionStorage.removeItem(CART_SWAP_GUARD_KEY);
    } catch (e) { /* private mode */ }
}

function readSelectedStore() {
    try {
        return {
            id: localStorage.getItem('storeID') || '',
            oid: localStorage.getItem('storeOID') || '',
            name: localStorage.getItem('storeName') || '',
        };
    } catch (e) {
        return {id: '', oid: '', name: ''};
    }
}

function applySelectedStore(store) {
    if (!store || (!store.id && !store.oid)) return;
    const maxAge = 60 * 60 * 24 * CART_COOKIE_DAYS;
    document.cookie = `storeID=${encodeURIComponent(store.oid || '')}; path=/; domain=${CART_ROOT_DOMAIN}; SameSite=None; Secure; max-age=${maxAge}`;
    document.cookie = `storeName=${encodeURIComponent(store.name || '')}; path=/; domain=${CART_ROOT_DOMAIN}; SameSite=None; Secure; max-age=${maxAge}`;
    try {
        localStorage.setItem('storeID', String(store.id || ''));
        localStorage.setItem('storeOID', String(store.oid || store.id || ''));
        localStorage.setItem('storeName', String(store.name || ''));
    } catch (e) { /* private mode */ }
}

function sameStore(a, b) {
    return (a && a.oid) === (b && b.oid) && (a && a.id) === (b && b.id);
}

// Returns the cart token stored against the customer, or '' when none.
// Never throws: a failed lookup must not break the page, it just means we
// fall back to whatever cart this device already has.
async function fetchStoredCart(customerId) {
    try {
        const response = await fetch('/apps/apis/api/product/get_customer_cart_metafield', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({customerId}),
            keepalive: true,
        });
        if (!response.ok) return '';
        const result = await response.json();
        // Verified against the live proxy: the read endpoint surfaces
        // persistentCart.items as `result`, so the token comes back there.
        // Legacy records hold an item array or the "nullcart" sentinel - both
        // mean "no token", so only a plain non-sentinel string counts.
        const value = result && result.result;
        if (typeof value !== 'string' || !value || value === NO_CART_SENTINEL) return null;
        try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed.token === 'string' && parsed.token) return parsed;
        } catch (e) { /* legacy record */ }
        return null;
    } catch (e) {
        return '';
    }
}

// Pass '' as the token to clear the pointer (cart emptied or order placed).
async function storeCartToken(customerId, token) {
    try {
        await fetch('/apps/apis/api/product/update_customer_cart_metafield', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                customerId,
                // The proxy only reads back persistentCart.items, so the token
                // has to travel in `items`. Keeping the existing "nullcart"
                // sentinel for "no cart" so old and new records read alike.
                persistentCart: {
                    items: token
                        ? JSON.stringify({token, store: readSelectedStore()})
                        : NO_CART_SENTINEL,
                },
            }),
            keepalive: true,
        });
    } catch (e) {
        // Losing a pointer write is recoverable - the next cart action retries.
        console.error('Persistent cart: failed to store cart token', e);
    }
}

/**
 * Fold products the shopper added while signed out into the account's cart.
 *
 * The account cart is authoritative for everything it already holds, so a
 * quantity changed while signed out reverts, and a product removed while
 * signed out comes back. Only variants the account does not have at all are
 * carried over - additions propagate, edits and deletions do not.
 *
 * Must run after the cookie has been switched to the account cart, so that
 * /cart.js and /cart/add.js both address that cart.
 */
function getAutoAddedVariantIds() {
    const t = window.ShopifyTranslations || {};
    return new Set(
        [t.palletProductID, t.surchargeProductID]
            .map((gid) => String(gid || '').replace('gid://shopify/ProductVariant/', '').trim())
            .filter(Boolean),
    );
}

async function mergeNewLinesIntoAccountCart(guestItems) {
    // Pallet deposit and fuel surcharge lines are added by the storefront
    // itself from the delivery method and store, and are recalculated for
    // whichever cart is active. Carrying them over would plant a stale
    // surcharge in the account's cart that nothing asked for - the same reason
    // the reorder flow excludes them.
    const autoAdded = getAutoAddedVariantIds();
    const incoming = (Array.isArray(guestItems) ? guestItems : []).filter(
        (item) => !autoAdded.has(String(item && item.variant_id)),
    );
    if (!incoming.length) return;

    try {
        const response = await fetch('/cart.js', {headers: {Accept: 'application/json'}});
        if (!response.ok) return;
        const accountCart = await response.json();
        const alreadyThere = new Set(
            (accountCart.items || []).map((item) => String(item.variant_id)),
        );

        const additions = incoming
            .filter((item) => !alreadyThere.has(String(item.variant_id)))
            .map((item) => ({
                id: item.variant_id,
                quantity: item.quantity,
                properties: item.properties || {},
            }));

        if (!additions.length) return;

        await fetch('/cart/add.js', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({items: additions}),
        });
    } catch (e) {
        console.error('Persistent cart: failed to merge signed-out additions', e);
    }
}


/**
 * Keeps the `cart` cookie in step with the real cart, and - for a logged-in
 * customer - with the cart token stored against their account.
 *
 * Resolution rule: on load the stored pointer wins, so a device that is on a
 * different cart adopts the account's cart. Once both devices share a token
 * they mutate the same Shopify cart, so they stop diverging and the pointer
 * only moves again when a genuinely new cart is created.
 */
async function resetCookies() {
    let cart;
    try {
        cart = await readCurrentCart();
    } catch (e) {
        console.error('Cart sync error:', e);
        return;
    }

    const customerId = getPersistentCartCustomerId();
    const isCheckedOut = window.location.pathname.includes('/thank_you');
    const isEmpty = !cart || cart.item_count === 0;

    // Signed-in customer changed since the last load. Write the new identity
    // first so the reload below cannot detach a second time.
    const lastCustomerId = readLastCustomerId();
    let justSignedIn = false;
    if (lastCustomerId !== customerId) {
        writeLastCustomerId(customerId);
        clearAdoptionGuard();
        if (lastCustomerId) {
            await detachFromAccountCart(cart);
            return;
        }
        // Guest -> signed in: anything added while signed out should follow.
        justSignedIn = Boolean(customerId);
    }

    // Belt and braces: if we are not signed in but are still holding a cart
    // this device last used while signed in, we were signed out without that
    // transition ever being observed here - the shopper may have used only the
    // account app, where this script does not load. Detach anyway, or every
    // edit made now lands on the account's cart and reaches their other
    // devices.
    if (!customerId && cart && cart.token && readAccountToken() === cart.token) {
        writeAccountToken('');
        await detachFromAccountCart(cart);
        return;
    }

    // Order placed: drop the cookie and the pointer so the next visit does
    // not resume a spent cart.
    if (isCheckedOut) {
        clearCartCookie();
        clearAdoptionGuard();
        writeAccountToken('');
        if (customerId) await storeCartToken(customerId, '');
        return;
    }

    // Guests have no account to sync against - original cookie behaviour.
    if (!customerId) {
        if (isEmpty) clearCartCookie();
        else if (cart.token) writeCartCookieToken(cart.token);
        return;
    }

    const stored = await fetchStoredCart(customerId);
    const storedToken = stored ? stored.token : '';

    // Adopt the account's cart when this device is on a different one.
    // This has to run even when THIS device's cart is empty: a second device
    // that has not added anything yet is precisely the case to cover.
    if (storedToken && storedToken !== cart.token) {
        if (readAdoptionGuard() === storedToken) {
            // Already swapped to this token and still not on it, so the saved
            // cart is gone. Replace the pointer with whatever we hold.
            await storeCartToken(customerId, isEmpty ? '' : cart.token);
            if (isEmpty) clearCartCookie();
            return;
        }
        writeAdoptionGuard(storedToken);
        // Bring the saved store across, otherwise a Store-Pickup cart can
        // land on a device pointing at a different branch.
        applySelectedStore(stored.store);

        // Capture the signed-out cart before switching away from it.
        const signedOutItems = justSignedIn ? (cart.items || []) : [];
        writeAccountToken(storedToken);
        writeCartCookieToken(storedToken);
        await mergeNewLinesIntoAccountCart(signedOutItems);
        window.location.reload();
        return;
    }

    // On the account's cart, or the account has none saved yet.
    if (!isEmpty && cart.token) {
        writeAccountToken(cart.token);
        writeCartCookieToken(cart.token);
        // Publish when there is no pointer, or when only the store moved
        // (changing branch does not create a new cart).
        if (!storedToken || !sameStore(stored && stored.store, readSelectedStore())) {
            await storeCartToken(customerId, cart.token);
        }
        return;
    }

    // Empty cart. If the pointer matches, the shared cart is simply empty and
    // both devices should agree on that - leave it alone. Only tidy up when
    // the account has no cart saved at all.
    if (!storedToken) clearCartCookie();
}

resetCookies();
